import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import type { useReadAndWriteContentQueryDataCacheReadContentFragment$data } from './__generated__/useReadAndWriteContentQueryDataCacheReadContentFragment.graphql.ts';
import useReadAndWriteContentQueryDataCacheWriteContentFragment, {
	type useReadAndWriteContentQueryDataCacheWriteContentFragment$data,
} from './__generated__/useReadAndWriteContentQueryDataCacheWriteContentFragment.graphql.ts';

export const useReadAndWriteContentQueryDataCache = ({ query, contentId, spaceKey }) => {
	const readQueryInterop =
		useReadFragmentInterop<useReadAndWriteContentQueryDataCacheReadContentFragment$data>({
			fragmentSpec: graphql`
				fragment useReadAndWriteContentQueryDataCacheReadContentFragment on Content {
					body {
						dynamic {
							representation
							value
							webresource {
								superbatch {
									tags {
										js
										css
									}
								}
								tags {
									js
									css
								}
							}
						}
					}
				}
			`,
		});
	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useReadAndWriteContentQueryDataCacheWriteContentFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: useReadAndWriteContentQueryDataCacheWriteContentFragment,
		fragmentSpec: graphql`
			fragment useReadAndWriteContentQueryDataCacheWriteContentFragment on Content {
				body {
					dynamic {
						representation
						value
						webresource {
							superbatch {
								tags {
									js
									css
								}
							}
							tags {
								js
								css
							}
						}
					}
				}
			}
		`,
	});

	const readAndWriteQueryCache = useCallback(
		(newAdfDoc) => {
			const contentQueryResultCache = readQueryInterop({
				id: `Content:${contentId}`,
			});

			// deep clone to allow content to be mutable
			const content = JSON.parse(JSON.stringify(contentQueryResultCache));
			const contentBody = content?.body?.dynamic || null;

			if (contentBody && contentQueryResultCache) {
				contentBody.value = JSON.stringify(newAdfDoc);
				writeFragmentInterop({
					fragmentId: `Content:${contentId}`,
					data: content,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[query, contentId, spaceKey, readQueryInterop, writeFragmentInterop],
	);

	return readAndWriteQueryCache;
};
